/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Form,
  Row,
  Spin,
  Typography,
  message,
  notification,
  Modal,
  Progress,
  Select,
  Input,
} from 'antd';
import {
  CheckCircleTwoTone,
  CheckOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  FileSearchOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { API_HOST } from 'utils/env';
import Table from 'components/Table';
import { formatReceivedValues } from 'utils/formatValues';
import { getEmpleadosWithoutFilter } from 'api/estructuraOrganizacional/empleados';
import {
  getRecibosDeNomina,
  iniciarTimbrado,
  iniciarConsulta,
  iniciarCancelacionNomina,
  solicitarCancelacionRecibo,
  liberarBloqueoTimbrado,
  getProgresoProceso,
  MAX_CONSULTAS_SEGUIMIENTO,
  hayConsultaPendiente,
  CAPACIDADES,
} from 'api/nominas/timbrado';
import { seguirProceso } from 'api/nominas/pollingProceso';
import { DESENLACE } from 'api/nominas/seguimientoProceso';
import ModalConfirmation from 'components/ModalConfirmation';
import DatosTimbradoRecibo from 'components/Nominas/Timbrado/DatosTimbradoRecibo';
import { getPlazasOptimized } from 'api/catalogos/plazas';
import currencyFormatter from 'utils/currencyFormatter';
import { checkToken } from 'api/utils';
import {
  ESTATUS_TIMBRADO_TIMBRADO,
  ESTATUS_TIMBRADO_PENDIENTE_CONCILIAR,
} from 'utils/constants';
import EstatusTimbradoTag from 'components/Nominas/Timbrado/EstatusTimbradoTag';
import { puedeTimbrarTodo } from 'utils/estatusTimbrado';

// Con todos sus recibos pendientes de conciliar no hay nada que timbrar, y no se deben reenviar
const MENSAJE_NOMINA_PENDIENTE_CONCILIAR = 'Los recibos pendientes de esta nómina están por conciliar con el '
  + 'proveedor; no se deben volver a timbrar.';

const { Text } = Typography;

// Mínimo que exige el servidor para el motivo de la liberación (ticket 18)
export const MOTIVO_LIBERACION_MIN = 10;

// D4 (ticket 22): Timbrar Todo y Cancelar Todo exigen un permiso propio del timbrado, el
// mismo que valida el servidor (nominas/utils/permisos_timbrado.py en SAC).
export const PERMISO_TIMBRAR_TODO = { model: 'nominaplazatimbrado', action: 'autorizar' };
export const PERMISO_CANCELAR_TODO = { model: 'nominaplazatimbrado', action: 'cancelar' };

export const timbradoNominaDetallePermission = {
  permissionModel: 'nominaplaza',
  catalogsModels: [],
  tabsModels: [],
};

const TimbradoNominaDetalle = ({
  permission,
  selected,
  encabezado,
  fetchEncabezado,
  onClickEdit,
  fetchData,
  estatusTimbrado,
}) => {
  const [data, setData] = useState([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [plazas, setPlazas] = useState([]);
  const [pageActual, setPageActual] = useState(1);
  // Ticket 3: búsqueda de la tabla y filtro por estatus de timbrado. En refs para que la
  // recarga al terminar un proceso (callback del seguimiento) los conserve.
  const [estatusFiltro, setEstatusFiltro] = useState(null);
  const consultaRef = useRef(null);
  const estatusRef = useRef(null);
  // Seguimiento del proceso de timbrado en background
  // procesoActivo: { proceso, titulo, mensajeExito } | null
  const [procesoActivo, setProcesoActivo] = useState(null);
  const [progresoProceso, setProgresoProceso] = useState({ en_curso: 0, total: 0 });
  // Si ya se vio el proceso en curso (habilita o no "Dejar de Esperar")
  const [inicioConfirmado, setInicioConfirmado] = useState(false);
  // Acción pendiente de confirmación:
  // 'consultar' | 'timbrar' | 'cancelarPlaza' | 'cancelarTodo' | null
  const [accionPorConfirmar, setAccionPorConfirmar] = useState(null);
  // Liberación manual del bloqueo de Timbrar Todo (ticket 18): motivo capturado, o null si cerrado
  const [motivoLiberacion, setMotivoLiberacion] = useState(null);
  // Recibo cuyo panel de Datos de Timbrado está abierto
  const [reciboDatosTimbrado, setReciboDatosTimbrado] = useState(null);

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
        nombre_empleado: e?.empleado,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  // La tabla llama con (página, búsqueda); sin búsqueda (recarga) se reutiliza la última
  const fetchPaginated = async (page = 1, q) => {
    if (q !== undefined) consultaRef.current = q;
    setLoading(true);
    const cleanPage = Number(String(page).replace(/\D/g, '')) || 1; // solo dígitos
    const response = await getRecibosDeNomina(
      selected,
      cleanPage,
      consultaRef.current,
      estatusRef.current,
    );
    if (response) {
      const results = formatReceivedClone(response.results);
      setSelectedRowKeys([]);
      setPageActual(cleanPage);
      setData({ ...response, results });
    }
    setLoading(false);
  };

  const onChangeEstatusFiltro = (estatus) => {
    estatusRef.current = estatus ?? null;
    setEstatusFiltro(estatus ?? null);
    fetchPaginated(1);
  };

  useEffect(() => {
    let mounted = true;
    // Otra nómina: el filtro por estatus no se arrastra
    estatusRef.current = null;
    setEstatusFiltro(null);
    setPageActual(1);
    const fetchAll = async () => {
      await checkToken();
      setLoading(true);
      const [
        // _empleados,
        // _plazas,
        _data,
      ] = await Promise.all([
        // getEmpleadosWithoutFilter(),
        // getPlazasOptimized(),
        // La búsqueda capturada en la tabla se conserva al cambiar de nómina
        getRecibosDeNomina(selected, 1, consultaRef.current),
      ]);
      if (mounted) {
        // setEmpleados(_empleados);
        // setPlazas(_plazas);
        if (_data) {
          setData({ ..._data, results: formatReceivedClone(_data.results) });
        }
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [selected]);

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  // Aviso al usuario según cómo terminó el seguimiento del proceso
  const finalizarSeguimiento = ({ titulo, mensajeExito }, desenlace, errores = []) => {
    setProcesoActivo(null);
    setProgresoProceso({ en_curso: 0, total: 0 });
    setInicioConfirmado(false);

    const avisos = {
      [DESENLACE.ERRORES_CONSULTA]: ['warning', 'No se pudo consultar el progreso; el proceso puede seguir '
        + 'en cola o en ejecución. Consulte más tarde antes de volver a lanzarlo.'],
      [DESENLACE.ERRORES_PROCESO]: ['warning', errores.join(' | ')],
      [DESENLACE.TERMINADO]: ['success', mensajeExito],
      [DESENLACE.INICIO_NO_CONFIRMADO]: ['warning', 'No se pudo confirmar el inicio del proceso; '
        + 'puede seguir en cola. Consulte más tarde antes de volver a lanzarlo.'],
      [DESENLACE.ESPERA_ABANDONADA]: ['warning', 'Se dejó de esperar sin confirmar el inicio del '
        + 'proceso: puede seguir en cola o haber terminado. Revise el estatus de la nómina antes '
        + 'de volver a lanzarlo.'],
      [DESENLACE.SIGUE_EN_EJECUCION]: ['warning', 'El proceso sigue en ejecución. Consulte '
        + 'nuevamente más tarde.'],
    };
    const [tipo, description] = avisos[desenlace] || avisos[DESENLACE.SIGUE_EN_EJECUCION];
    notification[tipo]({
      message: titulo,
      description,
      placement: 'topRight',
      duration: 0,
    });

    // Refrescar encabezado, listado de nóminas y plazas de la nómina
    fetchEncabezado(selected);
    fetchData();
    fetchPaginated(pageActual);
  };

  // Polling del progreso del proceso de timbrado en curso
  useEffect(() => {
    if (!procesoActivo) return () => {};
    setInicioConfirmado(false);

    // Al desmontar o al cambiar procesoActivo se detiene; respuestas tardías se ignoran
    return seguirProceso({
      consultar: (opciones) => getProgresoProceso(procesoActivo.proceso, opciones),
      maxConsultas: MAX_CONSULTAS_SEGUIMIENTO,
      onAvance: ({ enCurso, total }) => setProgresoProceso({ en_curso: enCurso, total }),
      onInicioConfirmado: () => setInicioConfirmado(true),
      onFin: (desenlace, progreso) => finalizarSeguimiento(
        procesoActivo,
        desenlace,
        progreso.errores,
      ),
    });
    // eslint-disable-next-line
  }, [procesoActivo]);

  // Mientras no se confirme el inicio, el usuario puede dejar de esperar (p. ej. si
  // el proceso terminó entre dos consultas y la fila quedó igual a la previa).
  // Nunca se anuncia "terminado": solo se detiene y se refrescan los datos.
  const onDejarDeEsperar = () => {
    if (procesoActivo && !inicioConfirmado) {
      finalizarSeguimiento(procesoActivo, DESENLACE.ESPERA_ABANDONADA);
    }
  };

  const onClickConsultar = () => {
    if (!hayConsultaPendiente(encabezado)) {
      message.info('No hay una consulta de timbrado pendiente para esta nómina.');
      return;
    }
    setAccionPorConfirmar('consultar');
  };

  const onClickTimbrar = () => {
    if (encabezado?.tipo_de_estatus_timbrado === ESTATUS_TIMBRADO_TIMBRADO) {
      message.info('La nómina ya fue timbrada correctamente en todos sus registros.');
      return;
    }
    if (encabezado?.tipo_de_estatus_timbrado === ESTATUS_TIMBRADO_PENDIENTE_CONCILIAR) {
      message.info(MENSAJE_NOMINA_PENDIENTE_CONCILIAR);
      return;
    }
    if (!encabezado.nomina_termino_servicio_timbrado) {
      setAccionPorConfirmar('timbrar');
    }
  };

  const onClickCancelarTodo = () => {
    setAccionPorConfirmar('cancelarTodo');
  };

  const onClickLiberarBloqueo = () => setMotivoLiberacion('');

  const confirmarLiberacion = async () => {
    setLoading(true);
    try {
      const { liberado, mensaje } = await liberarBloqueoTimbrado(
        encabezado?.id,
        motivoLiberacion.trim(),
      );
      if (liberado) {
        setMotivoLiberacion(null);
        message.success(mensaje || 'Se liberó el bloqueo de timbrado de la nómina.');
        await fetchEncabezado?.(selected);
      }
    } finally {
      setLoading(false);
    }
  };

  const onClickCancelarPlaza = () => {
    if (!selectedRowKeys.length) {
      message.info('Seleccione un recibo de la tabla para cancelar.');
      return;
    }
    setAccionPorConfirmar('cancelarPlaza');
  };

  const onClickDatosTimbrado = () => {
    const recibo = data?.results?.find((e) => e.id === selectedRowKeys[0]);
    if (!recibo) {
      message.info('Seleccione un recibo de la tabla para consultar sus datos de timbrado.');
      return;
    }
    setReciboDatosTimbrado(recibo);
  };

  const tituloConfirmacion = () => {
    const numeroNomina = encabezado?.numero_nomina || '';
    switch (accionPorConfirmar) {
      case 'consultar':
        return `Consultar el estatus de timbrado de la Nómina ${numeroNomina}`;
      case 'timbrar':
        return `Timbrar todos los recibos de la Nómina ${numeroNomina}`;
      case 'cancelarTodo':
        return `Cancelar todos los recibos timbrados de la Nómina ${numeroNomina}`;
      case 'cancelarPlaza': {
        const recibo = data?.results?.find((e) => e.id === selectedRowKeys[0]);
        return `Cancelar el recibo timbrado del empleado ${recibo?.empleado_numero_y_nombre_completo || recibo?.numero_y_nombre_completo || ''}`;
      }
      default:
        return '';
    }
  };

  const ejecutarAccionConfirmada = async () => {
    const accion = accionPorConfirmar;
    const numeroNomina = encabezado?.numero_nomina || '';
    setAccionPorConfirmar(null);
    setLoading(true);
    try {
      // Si se lanzó un proceso en background se le da seguimiento; si no, se
      // muestra el mensaje informativo del backend (si lo hay).
      const iniciaSeguimiento = (inicio, titulo, mensajeExito) => {
        if (inicio.iniciado) {
          setProcesoActivo({ proceso: inicio.proceso, titulo, mensajeExito });
        } else if (inicio.procesoEnCurso) {
          // No se lanzó: ya había un proceso del mismo tipo en curso
          notification.warning({
            message: titulo,
            description: inicio.mensaje,
            placement: 'topRight',
          });
        } else if (inicio.mensaje) {
          message.info(inicio.mensaje);
        }
      };

      if (accion === 'consultar') {
        iniciaSeguimiento(
          await iniciarConsulta(encabezado?.id),
          'Consulta de Timbrado',
          `La consulta de timbrado de la nómina ${numeroNomina} ha finalizado.`,
        );
      } else if (accion === 'timbrar') {
        iniciaSeguimiento(
          await iniciarTimbrado(encabezado?.id),
          'Timbrado de Nómina',
          `La nómina ${numeroNomina} ha finalizado el timbrado.`,
        );
      } else if (accion === 'cancelarTodo') {
        iniciaSeguimiento(
          await iniciarCancelacionNomina(encabezado?.id),
          'Cancelación de Timbrado',
          `La cancelación de timbrado de la nómina ${numeroNomina} ha finalizado.`,
        );
      } else if (accion === 'cancelarPlaza') {
        const { mensaje } = await solicitarCancelacionRecibo(selectedRowKeys[0]);
        if (mensaje) {
          message.info(mensaje);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'No. de Plaza',
      dataIndex: 'plaza_no',
      key: 'plaza_no',
      width: 200,
    },
    {
      titleText: 'No. de Empleado',
      dataIndex: 'empleado_numero',
      key: 'empleado_numero',
      width: 200,
    },
    {
      titleText: 'Empleado',
      dataIndex: 'empleado_numero_y_nombre_completo',
      key: 'empleado_numero_y_nombre_completo',
      width: 300,
    },
    {
      titleText: 'Importe Neto',
      dataIndex: 'importe_neto',
      key: 'importe_neto',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Estatus de Timbrado',
      dataIndex: 'tipo_de_estatus_timbrado',
      key: 'tipo_de_estatus_timbrado',
      width: 200,
      render: (val) => <EstatusTimbradoTag id={val} catalogo={estatusTimbrado} />,
    },
    {
      titleText: 'Mensaje de Timbrado',
      dataIndex: 'mensaje_estatus_timbrado',
      key: 'mensaje_estatus_timbrado',
      width: 500,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Row style={{ marginBottom: 8 }}>
          <Select
            allowClear
            placeholder="Filtrar por Estatus de Timbrado"
            style={{ minWidth: 280 }}
            value={estatusFiltro ?? undefined}
            onChange={onChangeEstatusFiltro}
            options={(estatusTimbrado || []).map((estatus) => ({
              value: estatus.id,
              label: estatus.descripcion,
            }))}
          />
        </Row>
        <Table
          cols={columns}
          data={data?.results || []}
          count={data?.count || 0}
          currentPage={pageActual}
          rowSelection={rowSelection}
          pagination={false}
          paginationCallback={fetchPaginated}
          handleOnRowClick={handleOnRowClick}
          customActions={[
            {
              onClick: () => onClickConsultar(),
              text: 'Consultar',
              icon: <ExclamationCircleOutlined />,
            },
            {
              // D4 (ticket 22): el servidor exige el mismo permiso; ocultarla aquí solo es UX
              permission: PERMISO_TIMBRAR_TODO,
              onClick: () => onClickTimbrar(),
              text: 'Timbrar Todo',
              icon: <CheckOutlined />,
              // Se bloquea cuando todos los registros ya fueron timbrados correctamente, o cuando
              // lo único pendiente son recibos por conciliar (nunca se reenvían)
              disabled: !puedeTimbrarTodo(encabezado?.tipo_de_estatus_timbrado),
              tooltip: encabezado?.tipo_de_estatus_timbrado === ESTATUS_TIMBRADO_PENDIENTE_CONCILIAR
                ? MENSAJE_NOMINA_PENDIENTE_CONCILIAR
                : undefined,
            },
            {
              permission: { model: 'nominaplaza', action: 'cancelar' },
              onClick: () => onClickCancelarPlaza(),
              text: 'Cancelar',
              icon: <CloseOutlined />,
              // Deshabilitada mientras el backend no soporte cancelar un recibo individual
              disabled: !CAPACIDADES.cancelacionIndividual,
              tooltip: CAPACIDADES.cancelacionIndividual
                ? undefined
                : 'La cancelación individual de recibos aún no está disponible',
            },
            {
              permission: PERMISO_CANCELAR_TODO,
              onClick: () => onClickCancelarTodo(),
              text: 'Cancelar Todo',
              icon: <CloseOutlined />,
            },
            {
              // Mismo permiso que Timbrar Todo; el servidor solo libera bloqueos sin
              // actividad reciente
              permission: PERMISO_TIMBRAR_TODO,
              onClick: () => onClickLiberarBloqueo(),
              text: 'Liberar Bloqueo',
              icon: <UnlockOutlined />,
              tooltip: 'Libera una nómina cuyo timbrado quedó atascado (sin actividad reciente)',
            },
            {
              permission: { model: 'nominaplazatimbrado', action: 'view' },
              onClick: () => onClickDatosTimbrado(),
              text: 'Datos de Timbrado',
              icon: <FileSearchOutlined />,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
        />
        <div style={{
          display: 'flex', alignItems: 'center', position: 'absolute', bottom: 0, right: 0,
        }}
        >
          <CheckCircleTwoTone
            twoToneColor="#d88f4f"
            style={{
              fontSize: '23px',
              marginRight: '5px',
            }}
          />
          <Text style={{ fontSize: '0.8rem', color: '#d88f4f' }}>
            {encabezado?.tiempo_de_timbrado}
          </Text>
        </div>
      </Spin>
      <ModalConfirmation
        onFinish={ejecutarAccionConfirmada}
        Confirm
        onCancel={() => setAccionPorConfirmar(null)}
        open={!!accionPorConfirmar}
        contentTitle={tituloConfirmacion()}
        loading={loading}
      />
      <Modal
        open={motivoLiberacion !== null}
        title={`Liberar el Bloqueo de Timbrado de la Nómina ${encabezado?.numero_nomina || ''}`}
        okText="Liberar"
        cancelText="Cancelar"
        onOk={confirmarLiberacion}
        onCancel={() => setMotivoLiberacion(null)}
        okButtonProps={{ disabled: (motivoLiberacion || '').trim().length < MOTIVO_LIBERACION_MIN }}
        confirmLoading={loading}
        destroyOnClose
      >
        <p>
          Use esta opción solo si el timbrado quedó atascado. Los recibos que estaban en envío
          quedan Pendientes de Conciliar y no se reenvían.
        </p>
        <Form layout="vertical">
          <Form.Item label="Motivo de la Liberación" required>
            <Input.TextArea
              aria-label="Motivo de la Liberación"
              rows={3}
              maxLength={500}
              value={motivoLiberacion || ''}
              onChange={(e) => setMotivoLiberacion(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
      <DatosTimbradoRecibo
        visible={!!reciboDatosTimbrado}
        onClose={() => setReciboDatosTimbrado(null)}
        recibo={reciboDatosTimbrado}
        estatusTimbrado={estatusTimbrado}
      />
      <Modal
        open={!!procesoActivo}
        title={procesoActivo?.titulo || 'Procesando...'}
        footer={inicioConfirmado ? null : (
          <Button onClick={onDejarDeEsperar}>Dejar de Esperar</Button>
        )}
        closable={false}
        maskClosable={false}
        centered
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {progresoProceso.total > 0 ? (
            <Progress
              type="circle"
              percent={Math.round((progresoProceso.en_curso / progresoProceso.total) * 100)}
            />
          ) : (
            <Spin size="large" />
          )}
          <p style={{ marginTop: 16 }}>
            {progresoProceso.total > 0
              ? `${progresoProceso.en_curso} / ${progresoProceso.total} registros procesados`
              : 'Procesando, por favor espere...'}
          </p>
          {!inicioConfirmado && (
            <Text type="secondary">
              Esperando a que el proceso inicie. Si terminó muy rápido puede no detectarse;
              en ese caso deje de esperar y revise el estatus de la nómina.
            </Text>
          )}
        </div>
      </Modal>
    </Row>
  );
};
export default TimbradoNominaDetalle;
